<!--
  File name: DateComponent.vue
  Program Description:
    - This component handles the date component fields used within the system.
    - It allows the text field to format information into a text following a
    date format specified.
-->

<template>
  <v-menu
    ref="menu"
    v-model="$data.menu"
    :close-on-content-click="false"
    transition="scale-transition"
    offset-y
    min-width="290px"
    :disabled="disabled"
  >
    <template v-slot:activator="{ on: { click } }">
      <v-text-field
        :id="id"
        :label="label"
        :persistent-placeholder="persistentPlaceholder"
        :placeholder="placeholder"
        :append-icon="prepend ? '' : 'event'"
        :prepend-icon="prepend ? 'event' : ''"
        :class="dateClass"
        :disabled="disabled"
        :readonly="readonly"
        :outlined="outlined"
        :value="wholeMonth ? computedFormat : inputDate"
        :dense="isDense"
        :error-messages="errorMessages"
        :hide-details="hideDetails"
        :clearable="clearable"
        @click="openPicker"
        @input="saveDate"
        @click:append="click"
        @click:prepend="click"
        v-on="vOnTextField"
      />
    </template>
    <v-date-picker
      v-if="noTitle"
      ref="picker"
      v-model="inputDatePicker"
      :type="pickerType"
      :max="max"
      :min="min"
      no-title
      @click:year="saveDate($event)"
    />
    <v-date-picker
      v-else
      ref="picker"
      v-model="inputDatePicker"
      :max="max"
      :min="min"
      :type="pickerType"
      @change="saveDate"
    />
  </v-menu>
</template>

<script>
import { format, parseISO } from 'date-fns'

export default {
  model: {
    prop: 'val',
    event: 'input'
  },
  props: {
    min: {
      type: String,
      default: ''
    },
    max: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'YYYY-MM-DD'
    },
    pickerType: {
      type: String,
      default: 'date'
    },
    id: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    value: {
      type: [String, Number],
      default: ''
    },
    dateClass: {
      type: String,
      default: ''
    },
    clearable: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: true
    },
    readonly: {
      type: Boolean,
      default: false
    },
    openPickerOnClick: {
      type: Boolean,
      default: false
    },
    outlined: {
      type: Boolean,
      default: true
    },
    errorMessages: {
      type: Array,
      default: () => {}
    },
    isDense: {
      type: Boolean,
      default: true
    },
    noTitle: {
      type: Boolean,
      default: true
    },
    exactLength: {
      type: Number,
      default: 0
    },
    prepend: {
      type: Boolean,
      default: false
    },
    wholeMonth: {
      type: Boolean,
      default: false
    },
    yearOnly: {
      type: Boolean,
      default: false
    },
    vOnTextField: {
      type: Object,
      default: () => {}
    },
    hideDetails: {
      type: Boolean,
      default: false
    },
    persistentPlaceholder: {
      type: Boolean,
      default: true
    }
  },
  data: () => ({
    menu: false,
    inputDate: '',
    inputDatePicker: ''
  }),
  computed: {
    computedFormat () {
      return this.inputDatePicker ? format(parseISO(this.inputDatePicker), 'MMMM d yyyy') : ''
    }
  },
  watch: {
    inputDate (val) {
      if (val && val.toString().length === 4) {
        this.$emit('input', val)
      }
    },
    '$props.value' (val) {
      this.inputDate = this.$props.value
      if (this.validDate(val)) {
        this.inputDatePicker = this.$props.value
      }
    },
    menu (val) {
      if (this.yearOnly && val) {
        this.$nextTick(() => (this.$refs.picker.internalActivePicker = 'YEAR'))
      }
    }
  },
  created () {
    this.inputDate = this.$props.value
    if (this.validDate(this.$props.value)) {
      this.inputDatePicker = this.$props.value
    }
  },
  methods: {
    openPicker () {
      if (this.openPickerOnClick) {
        this.menu = true
      }
    },
    saveDate (date) {
      const dateString = typeof date === 'number' ? date.toString() : date
      this.inputDate = dateString
      this.$refs.menu.save(dateString)
      this.$emit('input', dateString)
      this.menu = false
    },
    validDate (value) {
      const validLength = !this.exactLength || (this.exactLength && value && value.length === this.exactLength)
      return ((value !== 'Invalid date' && validLength) || value === '')
    }
  }
}
</script>

<style scoped>
  .color-transparent >>> input {
    color: transparent !important
  }
</style>
